import type { SxProps, Theme } from '@mui/material';
import {
  BRAND_GRADIENT_END,
  BRAND_GRADIENT_START,
} from '../../theme/tokens';

/** Native system UI stack — reads as an app, not a desktop web page. */
export const MOBILE_FIELD_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const MOBILE_FIELD_CONTENT_PX = 2; // 16px at default 8px spacing unit

export const MOBILE_FIELD_BOTTOM_NAV_H = 60;

export const MOBILE_FIELD_RADIUS = {
  sm: 10,
  md: 12,
  lg: 16,
  pill: 999,
} as const;

export const MOBILE_FIELD_TYPE = {
  largeTitle: { fontSize: 28, fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.03em' },
  navTitle: { fontSize: 17, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
  body: { fontSize: 17, fontWeight: 400, lineHeight: 1.35, letterSpacing: '-0.01em' },
  bodyMedium: { fontSize: 17, fontWeight: 500, lineHeight: 1.35, letterSpacing: '-0.01em' },
  rowTitle: { fontSize: 17, fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.02em' },
  secondary: { fontSize: 15, fontWeight: 400, lineHeight: 1.3, letterSpacing: '-0.01em' },
  caption: { fontSize: 13, fontWeight: 400, lineHeight: 1.25 },
  mono: { fontSize: 14, fontWeight: 500, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
} as const;

/** Grouped list surface with a subtle Cnapse lavender tint. */
export function mobileFieldGroupedBg(theme: Theme): string {
  return theme.palette.mode === 'light' ? '#F3F1FA' : '#0A0A0F';
}

export const MOBILE_FIELD_BRAND_GRADIENT = `linear-gradient(90deg, ${BRAND_GRADIENT_START}, ${BRAND_GRADIENT_END})`;

export function mobileFieldBrandedHeaderSx(theme: Theme): Record<string, unknown> {
  return {
    ...mobileFieldHeaderBlurSx(theme),
    borderBottom: 'none',
    backgroundImage:
      theme.palette.mode === 'light'
        ? `linear-gradient(180deg, rgba(147, 136, 231, 0.14) 0%, rgba(255, 255, 255, 0.92) 48%)`
        : `linear-gradient(180deg, rgba(147, 136, 231, 0.22) 0%, rgba(28, 28, 30, 0.92) 52%)`,
  };
}

export function mobileFieldChromeSx(theme: Theme): SxProps<Theme> {
  return {
    fontFamily: MOBILE_FIELD_FONT_FAMILY,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    WebkitTapHighlightColor: 'transparent',
    bgcolor: mobileFieldGroupedBg(theme),
    color: theme.palette.text.primary,
  };
}

export function mobileFieldListCardSx(theme: Theme, selected?: boolean): SxProps<Theme> {
  return {
    mb: 0.75,
    px: 2,
    py: 1.5,
    borderRadius: `${MOBILE_FIELD_RADIUS.md}px`,
    border: 'none',
    bgcolor: 'background.paper',
    boxShadow:
      theme.palette.mode === 'light'
        ? '0 1px 2px rgba(0,0,0,0.04)'
        : '0 1px 0 rgba(255,255,255,0.06)',
    ...(selected
      ? {
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
        }
      : {}),
  };
}

export function mobileFieldSearchSx(): SxProps<Theme> {
  return {
    mx: 0,
    mb: 1.5,
    '& .MuiOutlinedInput-root': {
      borderRadius: `${MOBILE_FIELD_RADIUS.md}px`,
      minHeight: 44,
      bgcolor: 'background.paper',
      '& fieldset': { border: 'none' },
      boxShadow: (theme) =>
        theme.palette.mode === 'light'
          ? '0 1px 3px rgba(0,0,0,0.06)'
          : '0 1px 0 rgba(255,255,255,0.08)',
    },
    '& .MuiInputBase-input': {
      fontSize: MOBILE_FIELD_TYPE.body.fontSize,
      py: 1.25,
      px: 0.5,
    },
  };
}

export function mobileFieldHeaderBlurSx(theme: Theme): Record<string, unknown> {
  return {
    borderBottom: '1px solid',
    borderColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
    bgcolor:
      theme.palette.mode === 'light' ? 'rgba(255,255,255,0.82)' : 'rgba(28,28,30,0.82)',
    backdropFilter: 'saturate(180%) blur(16px)',
    WebkitBackdropFilter: 'saturate(180%) blur(16px)',
  };
}

export function mobileFieldFooterBlurSx(theme: Theme): Record<string, unknown> {
  return {
    borderTop: '1px solid',
    borderColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
    bgcolor:
      theme.palette.mode === 'light' ? 'rgba(255,255,255,0.9)' : 'rgba(28,28,30,0.9)',
    backdropFilter: 'saturate(180%) blur(16px)',
    WebkitBackdropFilter: 'saturate(180%) blur(16px)',
  };
}
